// Base configuration
import API_BASE_URL from "src/config/apiConfig";

const getAuthHeaders = (): HeadersInit => {
  const api_key = localStorage.getItem("api_key");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${api_key}`,
  };
};

// Helper function to handle fetch errors
const handleResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};

// Interfaces
export interface PageInfo {
  page_id: string;
  page_name: string;
  page_access_token: string;
}

export interface InstagramAccount {
  id: string;
  name: string;
}

export interface ExistingAccountDetails {
  facebook_access_token: string;
  facebook_ad_account_id: string;
  facebook_page_name: string;
  instagram_account_name: string;
}

export interface AdAccountsResponse {
  ad_account_ids: string[];
  pages: PageInfo[];
}

export interface TestConnectionParams {
  accessToken: string;
  adAccountId: string;
}

export interface CreateConnectionParams {
  accessToken: string;
  adAccountId: string;
  page_id: string;
  page_name: string;
  page_access_token: string;
  instagram_account_id: string;
  instagram_account_name: string;
  user_id: string | null;
  tenant_id: string | null;
  source: string;
}

// API functions
export const facebookService = {
  // 1. Fetch existing account details
  fetchExistingAccountDetails: async (
    tenant_id: string | null
  ): Promise<any> => {
    const response = await fetch(
      `${API_BASE_URL}/facebook/fetch-existing-account-details?tenant_id=${tenant_id}`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },

  // 2. Save token to server
  saveTokenToServer: async (
    token: string,
    userInfo: { name: string; email: string }
  ): Promise<any> => {
    const api_key = localStorage.getItem("api_key");
    const response = await fetch(`${API_BASE_URL}/facebook/receive-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${api_key}`,
      },
      body: `access_token=${token}&name=${encodeURIComponent(
        userInfo.name
      )}&email=${encodeURIComponent(userInfo.email)}`,
    });
    return handleResponse(response);
  },

  // 3. Fetch ad accounts and pages
  fetchAdAccounts: async (token: string): Promise<AdAccountsResponse> => {
    const api_key = localStorage.getItem("api_key");
    const response = await fetch(
      `${API_BASE_URL}/facebook/ad-accounts?access_token=${token}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${api_key}`,
        },
      }
    );
    return handleResponse(response);
  },

  // 4. Fetch Instagram account
  fetchInstagramAccount: async (
    pageId: string,
    pageAccessToken: string
  ): Promise<{ success: boolean; instagram_account?: InstagramAccount }> => {
    const response = await fetch(
      `${API_BASE_URL}/facebook/get-instagram-account`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          page_id: pageId,
          page_access_token: pageAccessToken,
        }),
      }
    );
    return handleResponse(response);
  },

  // 5. Test connection
  testConnection: async (params: TestConnectionParams): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/facebook/test-connection`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  // 6. Create connection
  createConnection: async (params: CreateConnectionParams): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/facebook/create-connection`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(params),
    });
    return handleResponse(response);
  },

  // 7. Remove connection
  removeConnection: async (tenant_id: string | null): Promise<any> => {
    const response = await fetch(
      `${API_BASE_URL}/remove-connection?tenant_id=${tenant_id}&source_name=facebook`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      }
    );
    return handleResponse(response);
  },
};
